import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import NightlifeIcon from '@mui/icons-material/Nightlife';
import { venueImageUrl } from '../../../api';
import FitImage from '../../../components/FitImage';
import { InviteResponseStatus, InviteStatus } from '../types';

// Header row from the reference screens: back arrow, centred title.
export function PageTop({ title, backTo, right }: { title: string; backTo?: string; right?: React.ReactNode }) {
  const navigate = useNavigate();
  return (
    <div className="hg-top">
      <button type="button" className="hg-back" aria-label="Back" onClick={() => (backTo ? navigate(backTo) : navigate(-1))}><ArrowBackIcon /></button>
      <h1>{title}</h1>
      <div>{right}</div>
    </div>
  );
}

const STATUS_LABEL: Record<string, string> = {
  Going: 'Going', Interested: 'Interested', Requested: 'Requested', Invited: 'Invited', Declined: 'Not going', Removed: 'Removed',
  Draft: 'Draft', Active: 'Active', Cancelled: 'Cancelled', Expired: 'Expired', Completed: 'Completed'
};

export function InviteStatusBadge({ status }: { status: InviteResponseStatus | InviteStatus }) {
  return <span className={`hg-badge ${status.toLowerCase()}`}>{STATUS_LABEL[status] ?? status}</span>;
}

export function Avatar({ initials, small }: { initials: string; small?: boolean }) {
  return <span className={`hg-avatar ${small ? 'sm' : ''}`} aria-hidden="true">{initials}</span>;
}

// Venue photo box (placeholder gradient when the venue has no photo yet).
export function VenueImage({ url, alt, className = 'hg-venue-img' }: { url?: string; alt: string; className?: string }) {
  const src = venueImageUrl(url);
  return <div className={className}>{src && <FitImage src={src} alt={alt} />}</div>;
}

export function EmptyState({ title, body, action }: { title: string; body: string; action?: React.ReactNode }) {
  return (
    <div className="hg-empty">
      <NightlifeIcon />
      <h2>{title}</h2>
      <p>{body}</p>
      {action}
    </div>
  );
}

export function ErrorState({ title, body }: { title: string; body: string }) {
  return (
    <div className="hg-card hg-empty" role="alert">
      <h2>{title}</h2>
      <p>{body}</p>
      <Link className="hg-btn hg-outline" to="/explore">Explore venues</Link>
    </div>
  );
}

export function Skeleton({ height, width = '100%', style }: { height: number; width?: number | string; style?: React.CSSProperties }) {
  return <div className="hg-skel" style={{ height, width, ...style }} aria-hidden="true" />;
}

export function SkeletonList({ rows = 4, height = 64 }: { rows?: number; height?: number }) {
  return (
    <div style={{ display: 'grid', gap: 10 }} aria-busy="true" aria-label="Loading">
      {Array.from({ length: rows }, (_, i) => <Skeleton key={i} height={height} />)}
    </div>
  );
}
